import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react"; 

import MakeSlider, { ISlider } from "../core/effects/slide";
import Container, { ElementNodeName } from "./Container";

type actionCallback = () => void;

interface IAccordion {
  children: ReactNode;
  expanded?: boolean;
  id?: string;
  className?: string;
  element?: ElementNodeName; // HTML block-level elements (for span, make sure to set display to block)
}

interface IAction_Any {
  children?: ReactNode,
}

interface IAction_Expand extends IAction_Any {
  onExpand?: actionCallback,
}

interface IAction_Collapse extends IAction_Any {
  onCollapse?: actionCallback,
}

interface IAccordionAction extends IAction_Expand, IAction_Collapse {}

const AccordionDataContext = createContext({
  isExpanded: false,
});

const AccordionApiContext = createContext({
  expand: () => {},
  collapse: () => {},
  toggle: () => {}
});

function useAccordionApiContext() {
  return useContext(AccordionApiContext);
}

function useAccordionDataContext() {
  return useContext(AccordionDataContext);
}

export function useAccordionApi() {
  return useAccordionApiContext();
}

export function useAccordionState() {
  return useAccordionDataContext();
}

export function withAccordionProvider(Component) {
  return function AccordionizedComponent(props): React.JSX.Element {
    return (
      <AccordionProvider>
        <Component {...props} />
      </AccordionProvider>
    )
  }
}

export function Accordion(props: IAccordion) {
  const { expanded, children, className, element = "div", ...rest } = props;

  const { expand, collapse } = useAccordionApiContext();
  const { isExpanded } = useAccordionDataContext();
  const isFirstLoad = useRef<boolean>(true);
  const elementRef = useRef<HTMLElement | null>(null);
  const [slider, setSlider] = useState<ISlider | null>(null);

  useEffect(() => {
    if(elementRef.current) {
      setSlider(MakeSlider(elementRef.current));
    }
  }, [elementRef.current]);

  useEffect(() => {
    if(expanded) {
      expand();
    } else if(!isFirstLoad.current) {
      collapse();
    }
  }, [expanded]);

  useEffect(() => {
    if(!slider || isFirstLoad.current) {
      return;
    }

    if(isExpanded) {
      slider.slideDown();
    } else {
      slider.slideUp();
    }
  }, [isExpanded, slider, isFirstLoad.current]);

  //return (isExpanded ? children : null);

  if(isFirstLoad.current === true) {
    isFirstLoad.current = false;

    return (
      expanded 
        ? <Container nodeName={element} className={className} ref={elementRef} {...rest}>{children}</Container> 
        : null
    );
  }

  return (
    <Container nodeName={element} className={className} ref={elementRef} {...rest}>
      {children}
    </Container>
  );
}

export function AccordionToggler({ children, onExpand, onCollapse, ...rest }: IAccordionAction) {
  const { expand, collapse } = useAccordionApiContext();
  const { isExpanded } = useAccordionDataContext();

  function handleClick(e) {
    e.preventDefault();

    if(isExpanded) {
      collapse();

      if(typeof onCollapse === "function") {
        onCollapse();
      }
    } else {
      expand();

      if(typeof onExpand === "function") {
        onExpand();
      }
    }
  }

  return (
    <span
      onClick={handleClick}
      style={{ display: "inherit", padding: 0, margin: 0 }}
      {...rest}
    >
      {children}
    </span>
  );
}

export function AccordionExpander({ children, onExpand, ...rest }: IAction_Expand) {
  const { expand } = useAccordionApiContext();

  function handleClick(e) {
    e.preventDefault();

    expand();

    if(typeof onExpand === "function") {
      onExpand();
    }
  }

  return (
    <span onClick={handleClick} {...rest}>
      {children}
    </span>
  );
}

export function AccordionCollapser({ children, onCollapse, ...rest }: IAction_Collapse) {
  const { collapse } = useAccordionApiContext();

  function handleClick(e) {
    e.preventDefault();

    collapse();

    if(typeof onCollapse === "function") {
      onCollapse();
    }
  }

  return (
    <span onClick={handleClick} {...rest}>
      {children}
    </span>
  );
}

export default function AccordionProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, {
    isExpanded: false,
  });

  const api = useMemo(() => {
    return {
      expand   : () => dispatch({ type: "expand" }),
      collapse : () => dispatch({ type: "collapse" }),
      toggle   : () => dispatch({ type: "toggle" }),
    }
  }, []);

  return (
    <AccordionDataContext.Provider value={state}>
      <AccordionApiContext.Provider value={api}>
        {children}
      </AccordionApiContext.Provider>
    </AccordionDataContext.Provider>
  );

  function reducer(state, action) {
    switch (action.type) {
      case "expand"   : return { ...state, isExpanded: true };
      case "collapse" : return { ...state, isExpanded: false };
      case "toggle"   : return { ...state, isExpanded: !state.isExpanded };
      default         : return state;
    }
  }
}


/**
 * USAGE Examples: 
 * 
 * 1. 
 *   import AccordionProvider, { Accordion, AccordionToggler } from "nbc-ui/accordion";
 *   <AccordionProvider>
 *     <AccordionToggler>
 *        {children}
 *     </AccordionToggler>
 *     
 *     <Accordion id="some-id" className="some-classname" expanded={true}>
 *       {children}
 *     </Accordion>
 *   </AccordionProvider>
 * 
 * 2. 
 *   import { Accordion, useAccordionApi, withAccordionProvider } from "nbc-ui/accordion";
 * 
 *   export default withAccordionProvider(MyComponent);
 *   
 *   function MyComponent() {
 *     const { toggle } = useAccordionApi();
 *     
 *     function handleToggleBtnClick(e: MouseEvent<HTMLButtonElement>) {
 *       e.preventDefault();
 *       toggle();
 *     }
 * 
 *     return (
 *       <React.Fragment>
 *         <button onClick={handleToggleBtnClick}>
 *           Click to toggle accordion
 *         </button>
 * 
 *         {...otherChildren}
 * 
 *       </React.Fragment>
 *     )
 *   }
 */

